import React, { useState, useEffect } from 'react';
import { ArrowLeft, GitCommit, Calendar, User, ExternalLink, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import githubApi from '../services/githubApi';

function CommitHistory() {
  const navigate = useNavigate();
  const [commits, setCommits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const commitsPerPage = 20;

  useEffect(() => {
    loadCommits(currentPage);
  }, [currentPage]);

  const loadCommits = async (page) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await githubApi.getCommits(page, commitsPerPage);
      setCommits(data);
      setHasMore(data.length === commitsPerPage);
    } catch (err) {
      console.error('Failed to load commits:', err);
      setError('Failed to load commit history. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const truncateMessage = (message, maxLength = 100) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 p-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="mb-6 flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> Back to Home
          </button>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Commits</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
            <button
              onClick={() => loadCommits(currentPage)}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> Back to Home
          </button>
          <h1 className="text-4xl font-bold flex items-center gap-3 mb-2">
            <GitCommit className="w-10 h-10 text-blue-500" />
            Commit History
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            View all commits and changes made to this repository
          </p>
        </div>

        {/* Commits List */}
        {isLoading ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Loading commits...</p>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              {commits.map((commit, index) => (
                <div
                  key={commit.sha}
                  className={`p-6 ${index !== commits.length - 1
                      ? 'border-b border-slate-200 dark:border-slate-700'
                      : ''
                    } hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors`}
                >
                  <div className="flex gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <img
                        src={commit.author?.avatar_url || commit.commit.author.avatar_url || 'https://github.com/ghost.png'}
                        alt={commit.commit.author.name}
                        className="w-12 h-12 rounded-full border-2 border-slate-200 dark:border-slate-600"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">
                            {truncateMessage(commit.commit.message.split('\n')[0])}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 flex-wrap">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {commit.commit.author.name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(commit.commit.author.date)}
                            </span>
                          </div>
                        </div>
                        <a
                          href={commit.html_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 text-slate-400 hover:text-blue-500 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          title="View on GitHub"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>

                      {/* Commit SHA */}
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-300">
                          {commit.sha.substring(0, 7)}
                        </code>
                        {commit.commit.message.split('\n').length > 1 && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            + {commit.commit.message.split('\n').length - 1} more line(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {(currentPage > 1 || hasMore) && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                      : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {currentPage}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!hasMore}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${!hasMore
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                      : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CommitHistory;
